import { useCallback } from "react";
import { API_BASE_URL } from "../constants/app.constant";
import { useFetch } from "../hooks/useFetch";
import { APIResponse } from "../interfaces/common.interface";
import { BackendOverallResponse, OverallResponseItem, EvaluationItem } from "../interfaces/overall-response.interface";
import useAuthService from "./auth.service";

const transformBackendResponse = (backendItem: EvaluationItem): OverallResponseItem => {
  return {
    corpus: backendItem.corpus || "",
    model: backendItem.model || "",
    clarity: backendItem.accuracy ?? 0,
    "a relevancy": backendItem.factuality ?? 0,
    "c relevancy": backendItem.relevance ?? 0,
    faithfulness: backendItem.faithfulness ?? 0,
    coherence: backendItem.coherence ?? 0,
    total_evaluations: backendItem.total_evaluations,
    last_run: backendItem.last_run
  };
};

/**
 * useOverallResponseService hook provides overall response evaluation API service functions.
 * Handles fetching and revalidating overall response metrics with data transformation.
 * 
 * @hook
 * @returns {Object} Overall response service methods
 * @returns {Function} returns.getOverallResponse - Get overall response metrics
 * @returns {Function} returns.revalidateOverallResponse - Trigger revalidation of metrics
 * 
 * @example
 * ```tsx
 * const { getOverallResponse, revalidateOverallResponse } = useOverallResponseService();
 * 
 * const data = await getOverallResponse();
 * await revalidateOverallResponse();
 * ```
 */
const useOverallResponseService = () => {
  const fetchService = useFetch();
  const { getUserAccessToken } = useAuthService();

  const OVERALL_RESPONSE_API = `${API_BASE_URL}/admin/overall-response`;

  /**
   * Gets overall response evaluation metrics.
   * Transforms backend response format to frontend format.
   * 
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<{data: OverallResponseItem[]; analysing: boolean} | undefined>} Transformed response data with analysing flag
   * @throws {Error} If token is invalid
   */
  const getOverallResponse = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const token = await getUserAccessToken();
        if (!token) throw new Error("Invalid token");

        const response = await fetchService.get<APIResponse<BackendOverallResponse>>(
          OVERALL_RESPONSE_API,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            signal,
          }
        );

        const backendData = response.data.evaluations || [];

        const analysing = response.data.analysing || false;
        
        const transformedData: OverallResponseItem[] = backendData.map(transformBackendResponse);

        return {
          data: transformedData,
          analysing 
        };
      } catch (error) {
        console.warn("API not available:", error);
      }
    },
    [fetchService, getUserAccessToken]
  );

  const REVALIDATE_API = `${API_BASE_URL}/admin/revalidate`;

  /**
   * Triggers revalidation of overall response metrics.
   * 
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<APIResponse<any>>} Revalidation response
   * @throws {Error} If token is invalid or revalidation fails
   */
  const revalidateOverallResponse = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const token = await getUserAccessToken();
        if (!token) throw new Error("Invalid token");

        const response = await fetchService.post<APIResponse<any>>(
          REVALIDATE_API,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            signal,
          }
        );

        return response;
      } catch (error) {
        console.error("Error revalidating overall response:", error);
        throw error;
      }
    },
    [fetchService, getUserAccessToken]
  );

  return {
    getOverallResponse,
    revalidateOverallResponse,
  };
};

export default useOverallResponseService;

